import { useMemo, useState } from "react";
import { Linkedin, Facebook, MessageCircle } from "lucide-react";
import { Reveal, SectionHead, PageHero } from "@/components/Shared";
import { INSIGHTS, EVENT } from "@/data/content";

const CATS = ["All", ...new Set(INSIGHTS.map((i) => i.cat))];

const Art = ({ seed }) => (
    <div className="relative h-44 overflow-hidden" aria-hidden="true"
        style={{ background: `linear-gradient(${120 + seed * 20}deg, #26070F 0%, #1A060C 55%, #2A0A12 100%)` }}>
        <div className="absolute inset-0 jaali opacity-40" />
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 176" fill="none" preserveAspectRatio="xMidYMid slice">
            <path d={`M${40 + seed * 8} 176 V${96 - seed * 4} Q${40 + seed * 8} ${50 - seed * 3} ${100 + seed * 6} ${50 - seed * 3} Q${160 + seed * 4} ${50 - seed * 3} ${160 + seed * 4} ${96 - seed * 4} V176`}
                stroke="rgba(201,162,75,0.5)" strokeWidth="1" />
            <path d={`M${220 - seed * 6} 176 V${110 - seed * 3} Q${220 - seed * 6} ${70 - seed * 2} ${268 - seed * 4} ${70 - seed * 2} Q${316 - seed * 2} ${70 - seed * 2} ${316 - seed * 2} ${110 - seed * 3} V176`}
                stroke="rgba(232,207,143,0.3)" strokeWidth="0.8" />
            <circle cx={330 - seed * 10} cy={40 + seed * 4} r={14 + seed} stroke="rgba(201,162,75,0.4)" strokeWidth="0.8" />
            <circle cx={330 - seed * 10} cy={40 + seed * 4} r="2.5" fill="var(--gold)" className="pulse-soft" />
            <line x1="0" y1={150 - seed * 5} x2="400" y2={150 - seed * 5} stroke="rgba(201,162,75,0.2)" strokeWidth="0.6" className="dash-flow" />
        </svg>
    </div>
);

export default function Insights() {
    const [cat, setCat] = useState("All");
    const list = useMemo(() => (cat === "All" ? INSIGHTS : INSIGHTS.filter((i) => i.cat === cat)), [cat]);
    const shareText = encodeURIComponent(`Startup MahaBazaar 2027 — ${EVENT.tagline}`);
    const url = encodeURIComponent(EVENT.website);

    return (
        <div data-testid="insights-page">
            <PageHero
                testid="insights-hero"
                eyebrow="MahaBazaar Insights"
                title={<>News & Stories from <span className="gold-text">the Bazaar</span></>}
                sub="Announcements, programme reveals, PitchYatra updates and innovation stories — the journal of the festival."
            />

            <section className="py-16 md:py-24">
                <div className="container-x">
                    <Reveal>
                        <div className="flex flex-wrap gap-2" data-testid="insights-filters">
                            {CATS.map((c) => (
                                <button key={c} onClick={() => setCat(c)} data-testid={`insights-filter-${c.toLowerCase().replace(/[\s/]+/g, "-")}`}
                                    className={`chip ${cat === c ? "chip-on" : ""}`}>
                                    {c}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {list.map((a, i) => (
                            <Reveal key={a.title} delay={(i % 4) * 60}>
                                <article className="card-luxe tilt h-full flex flex-col overflow-hidden" data-testid={`insight-card-${i}`}>
                                    <Art seed={a.art} />
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-center justify-between text-[0.55rem] tracking-[0.28em] uppercase">
                                            <span className="text-[var(--gold)]">{a.cat}</span>
                                            <span className="text-muted-luxe">{a.date}</span>
                                        </div>
                                        <h3 className="font-display text-lg mt-3 leading-snug text-[var(--ivory)]">{a.title}</h3>
                                        <p className="mt-3 text-xs text-muted-luxe leading-relaxed flex-1">{a.body}</p>
                                        <div className="mt-5 pt-4 border-t hairline flex items-center gap-4">
                                            <span className="text-[0.55rem] tracking-[0.3em] uppercase text-muted-luxe">Share</span>
                                            <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${url}`} target="_blank" rel="noopener noreferrer" data-testid={`insight-share-x-${i}`} className="text-[var(--muted)] hover:text-[var(--champagne)] transition-colors text-[0.6rem] tracking-[0.2em]">X</a>
                                            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" data-testid={`insight-share-linkedin-${i}`} className="text-[var(--muted)] hover:text-[var(--champagne)] transition-colors"><Linkedin size={12} /></a>
                                            <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" data-testid={`insight-share-facebook-${i}`} className="text-[var(--muted)] hover:text-[var(--champagne)] transition-colors"><Facebook size={12} /></a>
                                            <a href={`https://wa.me/?text=${shareText}%20${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" data-testid={`insight-share-whatsapp-${i}`} className="text-[var(--muted)] hover:text-[var(--champagne)] transition-colors"><MessageCircle size={12} /></a>
                                        </div>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 border-t hairline">
                <div className="container-x text-center">
                    <SectionHead eyebrow="Never Miss a Reveal" title={<>Get Insights in <span className="gold-text">Your Inbox</span></>}
                        sub="Subscribe in the footer below — programme reveals, PitchYatra updates and announcements before anyone else." />
                </div>
            </section>
        </div>
    );
}
